/**
 * The right pane — the AI chat slide-over (`090-ai-integration.md` §10, `110-design-system.md`
 * §5). *"Opening the AI panel with `Cmd+E` opens the pane if it was closed."*
 *
 * It used to be a `Backlinks | AI | …` tab strip, but backlinks live in the document flow below
 * the editor where the spec puts them (`070-search-and-links.md` §7.6), and the Details/Outline
 * tabs were never built — so the pane is simply the AI panel now. `ChatPanel` brings its own
 * header (New / Open-full), so there is no tab chrome above it.
 *
 * The pane's collapse handle and the `Cmd+E` hotkey live in `AppShell`; this component only owns
 * what is shown inside.
 */
import { Suspense, lazy } from 'react';
import { useTranslation } from '@/lib/i18n';

/**
 * The AI panel is LAZY (`120-non-functional.md` §2 — "the AI and Calendar routes are
 * lazy-loaded"). It brings the SSE parser, the markdown renderer and the whole conversation
 * tree with it, and the majority of sessions never open the pane. It loads the first time
 * `Mod+E` opens it, which is a keystroke the user has explicitly pressed — the one moment a
 * chunk fetch is invisible.
 */
const ChatPanel = lazy(async () => ({
  default: (await import('@/features/ai/ChatPanel')).ChatPanel,
}));

export function RightPanel({ onClose }: { onClose?: (() => void) | undefined }) {
  const { t } = useTranslation();

  return (
    <aside
      aria-label={t('shell.rightPanel.aria')}
      className="flex h-full min-h-0 flex-col bg-[var(--muted)]"
    >
      <Suspense fallback={<div className="h-full" aria-busy="true" />}>
        <ChatPanel onClose={onClose} />
      </Suspense>
    </aside>
  );
}
